import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  RefreshCw, 
  Star, 
  MoreHorizontal,
  CheckSquare,
  Square,
  Eye,
  EyeOff,
  User,
  Users,
  Mail,
  MailOpen,
  MessageSquare,
  MessageCircle,
  Send,
  ArrowRight
} from 'lucide-react';
import { Email, UserEmailFilter, EmailTypeFilter } from '../../types';
import { formatDate, getStatusColor, getStatusText, cn } from '../../utils';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import Pagination from '../ui/Pagination';
import EmailConversation from '../EmailConversation';
import { apiService } from '../../services/api';
import { getApiBaseUrl } from '../../config/env';

interface EmailListViewProps {
  emails: Email[];
  loading: boolean;
  selectedEmails: number[];
  onEmailSelect: (emailId: number) => void;
  onEmailClick: (email: Email) => void;
  onRefresh: () => void;
  onFilterChange: (filter: UserEmailFilter) => void;
  onEmailTypeFilterChange: (filter: EmailTypeFilter) => void;
  onShowHiddenToggle: (show: boolean) => void;
  userEmailFilter: UserEmailFilter;
  emailTypeFilter: EmailTypeFilter;
  showHidden: boolean;
  hiddenToggleLoading: boolean;
  pagination: {
    currentPage: number;
    total: number;
    limit: number;
    hasMore: boolean;
  };
  onPageChange: (page: number) => void;
}

const EmailListView: React.FC<EmailListViewProps> = ({
  emails,
  loading,
  selectedEmails,
  onEmailSelect,
  onEmailClick,
  onRefresh,
  onFilterChange,
  onEmailTypeFilterChange,
  onShowHiddenToggle,
  userEmailFilter,
  emailTypeFilter,
  showHidden,
  hiddenToggleLoading,
  pagination,
  onPageChange
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectAll, setSelectAll] = useState(false);
  const [markingAsRead, setMarkingAsRead] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'conversation'>('list');

  // 派生状态
  const conversationMode = viewMode === 'conversation';

  // 处理邮件状态变更
  const handleStatusChange = async (emailId: number, newStatus: string) => {
    try {
      await fetch(`${getApiBaseUrl()}/emails/${emailId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      // 通过父组件的刷新来更新
      onRefresh();
    } catch (error) {
      console.error('更新邮件状态失败:', error);
    }
  };

  // 处理回复
  const handleReply = (emailId: number) => {
    // TODO: 实现回复功能
    console.log('回复邮件:', emailId);
  };

  const filteredEmails = emails.filter(email =>
    email.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
    email.sender_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    email.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectAll = () => {
    if (selectAll) {
      // 取消全选
      filteredEmails.forEach(email => {
        if (selectedEmails.includes(email.id)) {
          onEmailSelect(email.id);
        }
      });
    } else {
      // 全选
      filteredEmails.forEach(email => {
        if (!selectedEmails.includes(email.id)) {
          onEmailSelect(email.id);
        }
      });
    }
    setSelectAll(!selectAll);
  };

  const handleMarkSelectedAsRead = async () => {
    if (selectedEmails.length === 0) return;
    
    setMarkingAsRead(true);
    
    try {
      // 批量标记选中的邮件为已读
      const promises = selectedEmails.map(emailId => 
        fetch(`${getApiBaseUrl()}/emails/${emailId}/status?status=read`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
        })
      );
      
      await Promise.all(promises);
      
      // 刷新邮件列表
      onRefresh();
      
      // 清除选择
      selectedEmails.forEach(emailId => onEmailSelect(emailId));
      
    } catch (error) {
      console.error('批量标记为已读失败:', error);
      alert('标记为已读失败，请重试');
    } finally {
      setMarkingAsRead(false);
    }
  };

  // 处理视图模式切换
  const handleViewModeChange = (mode: 'list' | 'conversation') => {
    setViewMode(mode);
  };

  // 渲染视图模式切换按钮
  const renderViewModeToggle = () => (
          <div className="flex items-center space-x-2 theme-bg-secondary rounded-lg p-1">
        <button
          onClick={() => handleViewModeChange('list')}
          className={cn(
            'px-3 py-1 text-sm rounded-md theme-transition',
            viewMode === 'list' 
              ? 'theme-bg-primary theme-text-primary theme-shadow-sm' 
              : 'theme-text-secondary hover:theme-text-primary'
          )}
        >
          <Mail className="w-4 h-4 inline mr-1" />
          列表
        </button>
        <button
          onClick={() => handleViewModeChange('conversation')}
          className={cn(
            'px-3 py-1 text-sm rounded-md theme-transition',
            viewMode === 'conversation' 
              ? 'theme-bg-primary theme-text-primary theme-shadow-sm' 
              : 'theme-text-secondary hover:theme-text-primary'
          )}
        >
          <MessageSquare className="w-4 h-4 inline mr-1" />
          对话
        </button>
      </div>
  );

  // 渲染筛选器
  const renderFilters = () => (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      {/* 用户邮件筛选 */}
      <div className="flex items-center space-x-2 theme-bg-secondary rounded-lg p-1">
        <button
          onClick={() => onFilterChange('all')}
          className={cn(
            'px-3 py-1 text-sm rounded-md theme-transition',
            userEmailFilter === 'all' 
              ? 'theme-bg-primary theme-text-primary theme-shadow-sm' 
              : 'theme-text-secondary hover:theme-text-primary'
          )}
        >
          <Users className="w-4 h-4 inline mr-1" />
          全部
        </button>
        <button
          onClick={() => onFilterChange('user')}
          className={cn(
            'px-3 py-1 text-sm rounded-md theme-transition',
            userEmailFilter === 'user' 
              ? 'theme-bg-primary theme-text-primary theme-shadow-sm' 
              : 'theme-text-secondary hover:theme-text-primary'
          )}
        >
          <User className="w-4 h-4 inline mr-1" />
          用户邮件
        </button>
        <button
          onClick={() => onFilterChange('non-user')}
          className={cn(
            'px-3 py-1 text-sm rounded-md theme-transition',
            userEmailFilter === 'non-user' 
              ? 'theme-bg-primary theme-text-primary theme-shadow-sm' 
              : 'theme-text-secondary hover:theme-text-primary'
          )}
        >
          <Mail className="w-4 h-4 inline mr-1" />
          系统邮件
        </button>
      </div>

      {/* 邮件类型筛选 */}
      <div className="flex items-center space-x-2 theme-bg-secondary rounded-lg p-1">
        <button
          onClick={() => onEmailTypeFilterChange('all')}
          className={cn(
            'px-3 py-1 text-sm rounded-md theme-transition',
            emailTypeFilter === 'all' 
              ? 'theme-bg-primary theme-text-primary theme-shadow-sm' 
              : 'theme-text-secondary hover:theme-text-primary'
          )}
        >
          <MessageCircle className="w-4 h-4 inline mr-1" />
          全部
        </button>
        <button
          onClick={() => onEmailTypeFilterChange('received')}
          className={cn(
            'px-3 py-1 text-sm rounded-md theme-transition',
            emailTypeFilter === 'received' 
              ? 'theme-bg-primary theme-text-primary theme-shadow-sm' 
              : 'theme-text-secondary hover:theme-text-primary'
          )}
        >
          <MailOpen className="w-4 h-4 inline mr-1" />
          收件箱
        </button>
        <button
          onClick={() => onEmailTypeFilterChange('sent')}
          className={cn(
            'px-3 py-1 text-sm rounded-md theme-transition',
            emailTypeFilter === 'sent' 
              ? 'theme-bg-primary theme-text-primary theme-shadow-sm' 
              : 'theme-text-secondary hover:theme-text-primary'
          )}
        >
          <Send className="w-4 h-4 inline mr-1" />
          已发送
        </button>
      </div>

      {/* 显示隐藏邮件切换 */}
      <button
        onClick={() => onShowHiddenToggle(!showHidden)}
        disabled={hiddenToggleLoading}
        className={cn(
          'px-3 py-1 text-sm rounded-md theme-transition border',
          showHidden 
            ? 'theme-yellow-bg theme-border-secondary theme-yellow-text hover:bg-yellow-100' 
            : 'theme-bg-secondary theme-border-secondary theme-text-secondary theme-bg-hover',
          hiddenToggleLoading && 'opacity-50 cursor-not-allowed'
        )}
      >
        {showHidden ? (
          <>
            <EyeOff className="w-4 h-4 inline mr-1" />
            隐藏系统邮件
          </>
        ) : (
          <>
            <Eye className="w-4 h-4 inline mr-1" />
            显示系统邮件
          </>
        )}
      </button>
    </div>
  );

  if (conversationMode) {
    return (
      <div className="theme-bg-primary rounded-lg theme-shadow-sm border theme-border-primary">
        {/* 头部工具栏 */}
        <div className="border-b theme-border-primary p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h2 className="text-lg font-semibold theme-text-primary">邮件对话</h2>
              {renderViewModeToggle()}
            </div>
            <Button
              onClick={onRefresh}
              disabled={loading}
              className="flex items-center space-x-2"
            >
              <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
              <span>刷新</span>
            </Button>
          </div>
        </div>

        {/* 筛选器 */}
        <div className="p-4 border-b theme-border-primary">
          {renderFilters()}
        </div>

        {/* 对话视图 */}
        <div className="p-4">
          <EmailConversation 
            emails={filteredEmails}
            subject="邮件对话"
            onStatusChange={handleStatusChange}
            onReply={handleReply}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="theme-bg-primary rounded-lg theme-shadow-sm border theme-border-primary">
      {/* 头部工具栏 */}
      <div className="border-b theme-border-primary p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-semibold theme-text-primary">邮件列表</h2>
            {renderViewModeToggle()}
          </div>
          <Button
            onClick={onRefresh}
            disabled={loading}
            className="flex items-center space-x-2"
          >
            <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
            <span>刷新</span>
          </Button>
        </div>

        {/* 筛选器 */}
        {renderFilters()}

        {/* 搜索栏 */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 theme-text-tertiary w-4 h-4" />
          <input
            type="text"
            placeholder="搜索邮件..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border theme-border-primary rounded-lg theme-input theme-transition"
          />
        </div>

        {/* 批量操作 */}
        {selectedEmails.length > 0 && (
          <div className="mt-4 flex items-center justify-between theme-blue-bg p-3 rounded-lg">
            <span className="text-sm theme-blue-text">
              已选择 {selectedEmails.length} 封邮件
            </span>
            <div className="flex items-center space-x-2">
              <Button
                onClick={handleMarkSelectedAsRead}
                disabled={markingAsRead}
                size="sm"
                className="theme-blue-button"
              >
                {markingAsRead ? '标记中...' : '标记为已读'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* 邮件列表 */}
      <div className="divide-y theme-border-primary">
        {/* 全选/取消全选 */}
        <div className="p-4 theme-bg-secondary border-b theme-border-primary">
          <div className="flex items-center">
            <button
              onClick={handleSelectAll}
              className="flex items-center space-x-2 text-sm theme-text-secondary hover:theme-text-primary theme-transition"
            >
              {selectAll ? (
                <CheckSquare className="w-4 h-4" />
              ) : (
                <Square className="w-4 h-4" />
              )}
              <span>{selectAll ? '取消全选' : '全选'}</span>
            </button>
            <span className="ml-4 text-sm theme-text-tertiary">
              共 {filteredEmails.length} 封邮件
            </span>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-32">
            <RefreshCw className="w-6 h-6 animate-spin theme-text-tertiary" />
          </div>
        ) : filteredEmails.length === 0 ? (
          <div className="flex justify-center items-center h-32">
            <p className="theme-text-tertiary">没有找到邮件</p>
          </div>
        ) : (
          <>
            {filteredEmails.map((email) => (
              <EmailListItem
                key={email.id}
                email={email}
                selected={selectedEmails.includes(email.id)}
                onSelect={() => onEmailSelect(email.id)}
                onClick={() => onEmailClick(email)}
              />
            ))}
          </>
        )}
      </div>

      {/* 分页 */}
      {pagination.total > 0 && (
        <div className="border-t theme-border-primary p-4">
          <Pagination
            currentPage={pagination.currentPage}
            hasMore={pagination.hasMore}
            total={pagination.total}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
};

interface EmailListItemProps {
  email: Email;
  selected: boolean;
  onSelect: () => void;
  onClick: () => void;
}

const EmailListItem: React.FC<EmailListItemProps> = ({ email, selected, onSelect, onClick }) => {
  const isUnread = email.status === 'unread';
  const isSent = email.email_type === 'sent';

  const handleSenderClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // TODO: 打开发件人规则配置弹窗
    console.log('打开发件人规则配置:', email.sender_email);
  };

  const getEmailPreview = (content: string) => {
    const textContent = content.replace(/<[^>]*>/g, '').trim();
    return textContent.length > 60 ? textContent.substring(0, 60) + '...' : textContent;
  };

  // 获取显示的时间
  const getDisplayTime = () => {
    if (isSent && email.sent_at) {
      return formatDate(email.sent_at);
    }
    return formatDate(email.received_at);
  };

  // 获取显示的联系人信息
  const getContactInfo = () => {
    if (isSent) {
      // 已发送邮件显示收件人信息
      return {
        name: email.recipient_name || email.recipient_email.split('@')[0],
        email: email.recipient_email,
        label: '收件人'
      };
    } else {
      // 接收邮件显示发件人信息
      return {
        name: email.sender_name || email.sender_email.split('@')[0],
        email: email.sender_email,
        label: '发件人'
      };
    }
  };

  const contactInfo = getContactInfo();

  return (
    <div
      className={cn(
        'px-4 py-3 theme-bg-hover cursor-pointer theme-transition border-l-4',
        selected && 'theme-blue-bg',
        isSent 
          ? 'theme-green-bg border-l-green-500'
          : isUnread 
            ? 'theme-blue-bg border-l-blue-500' 
            : 'border-l-transparent hover:border-l-gray-200'
      )}
      onClick={onClick}
    >
      {/* 第一行：邮件类型图标、联系人、时间 */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-3 min-w-0 flex-1">
          {/* 邮件类型图标 */}
          <div className="flex-shrink-0">
            {isSent ? (
              <Send className="w-4 h-4 text-green-600" />
            ) : (
              <div className={`w-2 h-2 rounded-full ${
                isUnread ? 'bg-blue-500' : 'bg-gray-300'
              }`} title={isUnread ? '未读邮件' : '已读邮件'}></div>
            )}
          </div>
          
          {/* 联系人信息 - 可点击 */}
          <button
            onClick={handleSenderClick}
            className={cn(
              'text-sm truncate max-w-40 text-left hover:theme-blue-text theme-transition flex items-center space-x-1',
              isUnread ? 'font-medium theme-text-primary' : 'font-normal theme-text-secondary'
            )}
            title={`配置${contactInfo.label}规则: ${contactInfo.email}`}
          >
            <span>{contactInfo.name}</span>
            {isSent && <ArrowRight className="w-3 h-3 theme-text-tertiary" />}
          </button>
        </div>
        
        {/* 时间 */}
        <div className="flex-shrink-0 ml-4">
          <span className={cn(
            "text-xs",
            isSent ? "theme-green-text" : "theme-text-tertiary"
          )}>
            {getDisplayTime()}
          </span>
        </div>
      </div>

      {/* 第二行：邮件标题和内容预览 */}
      <div className="space-y-1">
        {/* 邮件标题 - 粗体 */}
        <div className={cn(
          'text-sm font-bold truncate flex items-center space-x-2',
          isUnread ? 'theme-text-primary' : 'theme-text-secondary'
        )}>
          <span>{email.subject || '(无主题)'}</span>
          {isSent && (
            <Badge className="text-xs px-1 py-0 theme-green-bg theme-green-text border-green-200">
              已发送
            </Badge>
          )}
        </div>
        
        {/* 邮件内容预览 */}
        <div className="text-xs theme-text-tertiary truncate">
          {getEmailPreview(email.content)}
        </div>
      </div>
    </div>
  );
};

export default EmailListView; 